import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useEffect, useState } from "react"
import { useTarget } from "lib/@hooks/use-target"
import { range } from "lib/range"
import { checkForExcelErrors } from "./check-for-excel-errors"
import { ImportAssetsExcelFileSelected } from "event-definitions"
import { fileToBase64 } from "lib/file-to-base64"
import { Alert, Box, Card, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material"
import { SheetViewer } from "./sheet-viewer"

export function ChooseWorksheet({ setOptional, canContinue, setContinueFunction, optional }) {
    const target = useTarget()
    const [workbook, setWorkbook] = useBoundValue("workbook")
    const [, setFileName] = useBoundValue("fileName")
    const [, setFileContents] = useBoundValue("fileContents")
    const [sheet, setSheet] = useBoundValue("importFromSheet")
    const [processedSheet, setProcessedSheet] = useState(null)
    const [invalidCells, setInvalidCells] = useState([])
    const [isEmpty, setIsEmpty] = useState(false)

    useEffect(processSheet, [canContinue, setOptional, sheet, setContinueFunction])

    useEffect(() => {
        setOptional(optional)
    }, [optional, setOptional])

    ImportAssetsExcelFileSelected.useEvent(async ({ workbook, file }) => {
        setWorkbook(workbook)
        setFileContents(await fileToBase64(file))
        setFileName(file?.name)
    })

    return (
        <Box sx={{ pt: 2 }}>
            <FormControl fullWidth>
                <InputLabel id="worksheet-select-label">Select Excel Tab/Sheet</InputLabel>
                <Select
                    variant="outlined"
                    labelId="worksheet-select-label"
                    value={sheet ? sheet.name : ""}
                    label="Select Excel Tab/Sheet"
                    onChange={choose}
                    data-cy="asset-import-worksheet-select"
                >
                    {workbook?.worksheets.map((sheet, index) => (
                        <MenuItem
                            key={sheet.id}
                            value={sheet.name}
                            data-cy={`asset-import-worksheet-select-option-${index}`}
                        >
                            {sheet.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {processedSheet && (
                <Stack sx={{ mt: 3, mb: 1 }} spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                        Preview:
                    </Typography>
                    {isEmpty && (
                        <Alert severity="warning" data-cy="asset-import-empty-sheet-alert">
                            The selected worksheet contains no data rows. Please select a different worksheet or check
                            your file.
                        </Alert>
                    )}
                    {invalidCells.length > 0 && (
                        <Alert severity="error" data-cy="asset-import-invalid-cells-alert">
                            {buildInvalidCellsMessage(sheet?.name, invalidCells)}
                        </Alert>
                    )}
                    <Card>
                        <SheetViewer sx={{ maxHeight: 216 }} sheet={processedSheet} minRowCount={20} />
                    </Card>
                </Stack>
            )}
        </Box>
    )

    function processSheet() {
        if (sheet) {
            setContinueFunction(() => setOptional(sheet.name))
            const { sheet: cleanedSheet, invalidCells: foundInvalidCells } = checkForExcelErrors(sheet)
            const hasData = cleanedSheet.rowCount > 0
            setProcessedSheet(cleanedSheet)
            setInvalidCells(foundInvalidCells)
            setIsEmpty(!hasData)
            canContinue(foundInvalidCells.length === 0 && hasData)
        } else {
            canContinue(false)
            setProcessedSheet(null)
            setInvalidCells([])
            setIsEmpty(false)
        }
    }

    function choose(event) {
        target.fields = {}
        target.headerRow = null
        target.headers = null
        target.interpretedSheet = null
        target.uniqueValues = null
        target.previewOk = null
        target.existingMapping = null
        range(1, 5).forEach((step) => {
            target.ok[step] = null
        })

        const sheet = workbook?.worksheets.find((option) => option.name === event.target.value)
        setSheet(sheet)
    }
}

function buildInvalidCellsMessage(sheetName, invalidCells) {
    const samples = invalidCells.slice(0, 5).map(({ cell, error }) => `${cell} (${error})`)
    const sampleText = samples.join(", ")
    const remainingCount = invalidCells.length - samples.length
    const remainderText = remainingCount > 0 ? ` and ${remainingCount} more` : ""
    return `Cannot continue: "${sheetName}" contains ${invalidCells.length} Excel error cell${
        invalidCells.length === 1 ? "" : "s"
    } (${sampleText}${remainderText}). Please fix these cells in Excel and upload again.`
}
